import { useMemo, useState } from 'react';
import { Card, Tabs, theme } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, NodeIndexOutlined, SyncOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  useActionLogsQuery,
  useFlowExecutionStatusCountsQuery,
  useFlowExecutionsQuery,
  type ActionLogEntry,
  type ActionLogFilters,
  type EnrichedFlowExecution,
  type FlowExecutionFilters
} from '@/api/logs';
import type { Company } from '@/api/auth/interfaces';
import { StatCard } from '@/components/ui/StatCard';
import { StatCardGrid } from '@/components/ui/StatCardGrid';
import { getDateRangeFromPeriod } from '@/components/conversation-overview';
import { useAuth } from '@/hooks/useAuth';
import { useAllTenantsWithCompanies } from '@/hooks/useAllTenantsWithCompanies';
import { isAdminRole } from '@/utils/authz';
import { downloadCsv } from '@/utils/csvExport';
import { ActionLogsTable } from './ActionLogsTable';
import { FlowExecutionDrawer } from './FlowExecutionDrawer';
import { FlowExecutionsTable } from './FlowExecutionsTable';
import { LogsFilters } from './LogsFilters';
import type { AdminLogsSearch } from './searchSchema';
import { formatDuration } from '@/utils/dateFormat';

export interface AdminLogsViewProps {
  /** Filters, tab and page from the route's URL search params */
  search: AdminLogsSearch;
  onSearchChange: (patch: Partial<AdminLogsSearch>) => void;
  /** Scopes the whole view to one tenant (tenant logs page); omitted on the global page */
  fixedClientId?: string;
  /** Companies of the fixed tenant, for the audit scope selector */
  tenantCompanies?: Company[];
  /** Prefix for exported file names, e.g. the tenant key */
  exportName: string;
}

/** Search params reset by "Clear filters" (tab and page size are kept). */
const CLEARED_FILTERS: Partial<AdminLogsSearch> = {
  period: '7d',
  from: undefined,
  to: undefined,
  status: undefined,
  clientId: undefined,
  auditScope: undefined,
  search: undefined,
  page: 1
};

/**
 * Logs view shared by the global AI logs page and the per-tenant logs page:
 * execution KPIs, a flow executions tab (with detail drawer) and, for admins, an audit tab.
 */
export function AdminLogsView({ search, onSearchChange, fixedClientId, tenantCompanies, exportName }: AdminLogsViewProps) {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const { payload } = useAuth();
  const { tenantsById } = useAllTenantsWithCompanies(!fixedClientId);
  const [openExecution, setOpenExecution] = useState<EnrichedFlowExecution | null>(null);

  // The audit trail is ADMIN-only on the backend; developers only see executions
  const canSeeAudit = isAdminRole(payload?.role);
  const activeTab = canSeeAudit ? search.tab : 'executions';
  const clientId = fixedClientId ?? search.clientId;

  const dateRange = useMemo(() => {
    const customRange = search.from && search.to ? ([dayjs(search.from), dayjs(search.to)] as [dayjs.Dayjs, dayjs.Dayjs]) : null;
    return getDateRangeFromPeriod(search.period, customRange);
  }, [search.period, search.from, search.to]);
  const dateFrom = dateRange?.[0].toISOString();
  const dateTo = dateRange?.[1].toISOString();

  const executionScope = { clientId, dateFrom, dateTo };
  const executionFilters: FlowExecutionFilters = { ...executionScope, status: search.status, page: search.page, limit: search.pageSize };
  const executionsQuery = useFlowExecutionsQuery(executionFilters);
  const countsQuery = useFlowExecutionStatusCountsQuery(executionScope);

  // Audit entries are stored under the company ID when one exists, else the client ID
  const scopeCompanies = fixedClientId ? tenantCompanies ?? [] : clientId ? tenantsById.get(clientId)?.companies ?? [] : [];
  const auditScopeOptions = clientId
    ? [
        { value: clientId, label: t('logs.filters.tenantLevel') },
        ...scopeCompanies.map((company) => ({ value: company.id, label: t('logs.filters.company', { name: company.name }) }))
      ]
    : [];
  const auditFilters: ActionLogFilters = {
    page: search.page,
    limit: search.pageSize,
    search: search.search,
    tenant: clientId ? search.auditScope ?? clientId : undefined,
    dateFrom,
    dateTo
  };
  const auditQuery = useActionLogsQuery(auditFilters, canSeeAudit && activeTab === 'audit');

  const activeQuery = activeTab === 'audit' ? auditQuery : executionsQuery;
  // Only count filters that apply to the visible tab (status is executions-only, search and scope audit-only)
  const tabFilterActive = activeTab === 'audit' ? !!search.search || !!search.auditScope : !!search.status;
  const hasActiveFilters = search.period !== '7d' || tabFilterActive || (!fixedClientId && !!search.clientId);

  const handleRefresh = () => {
    void activeQuery.refetch();
    void countsQuery.refetch();
  };

  /** Downloads the rows currently shown in the active tab as CSV. */
  const handleExport = () => {
    const stamp = dayjs().format('YYYYMMDD-HHmm');
    if (activeTab === 'audit') {
      downloadCsv<ActionLogEntry>(
        `${exportName}-audit-${stamp}`,
        [
          { header: t('logs.audit.columns.time'), value: (row) => row.timestamp },
          { header: t('logs.audit.columns.user'), value: (row) => row.user_email },
          { header: t('logs.audit.columns.action'), value: (row) => row.action },
          { header: t('logs.audit.columns.entity'), value: (row) => row.id }
        ],
        auditQuery.data?.data ?? []
      );
      return;
    }
    downloadCsv<EnrichedFlowExecution>(
      `${exportName}-executions-${stamp}`,
      [
        { header: t('logs.executions.columns.time'), value: (row) => row.createdAt },
        { header: t('logs.executions.columns.status'), value: (row) => row.status },
        { header: t('logs.executions.columns.tenant'), value: (row) => row.tenant?.client.name },
        { header: t('logs.executions.columns.flow'), value: (row) => row.clientFlow?.name },
        { header: t('logs.drawer.template'), value: (row) => row.clientFlow?.masterFlow?.displayName },
        { header: t('logs.executions.columns.duration'), value: (row) => formatDuration(row.durationMs, 'precise') },
        { header: t('logs.executions.columns.error'), value: (row) => row.errorMessage },
        { header: t('logs.drawer.failedNode'), value: (row) => row.errorThrownInNode },
        { header: t('logs.drawer.executionId'), value: (row) => row.executionId },
        { header: t('logs.executions.columns.correlationId'), value: (row) => row.correlationId }
      ],
      executionsQuery.data?.data ?? []
    );
  };

  const handlePageChange = (page: number, pageSize: number) =>
    onSearchChange({ page: pageSize === search.pageSize ? page : 1, pageSize });

  const counts = countsQuery.data;
  const kpis = [
    { key: 'total', title: t('logs.kpi.total'), value: counts?.total, icon: <NodeIndexOutlined />, color: token.colorPrimary },
    { key: 'completed', title: t('logs.kpi.completed'), value: counts?.COMPLETED, icon: <CheckCircleOutlined />, color: token.colorSuccess },
    { key: 'failed', title: t('logs.kpi.failed'), value: counts?.FAILED, icon: <CloseCircleOutlined />, color: token.colorError },
    { key: 'running', title: t('logs.kpi.running'), value: counts?.STARTED, icon: <SyncOutlined />, color: token.colorInfo }
  ];

  return (
    <>
      <StatCardGrid>
        {kpis.map((kpi) => (
          <StatCard
            key={kpi.key}
            label={kpi.title}
            value={kpi.value ?? 0}
            icon={kpi.icon}
            color={kpi.color}
            loading={countsQuery.isLoading}
          />
        ))}
      </StatCardGrid>

      <LogsFilters
        values={{ ...search, tab: activeTab }}
        showTenantFilter={!fixedClientId}
        auditScopeOptions={auditScopeOptions}
        hasActiveFilters={hasActiveFilters}
        isFetching={activeQuery.isFetching}
        lastUpdatedAt={activeQuery.dataUpdatedAt || undefined}
        exportDisabled={!activeQuery.data?.data.length}
        onChange={onSearchChange}
        onClear={() => onSearchChange(CLEARED_FILTERS)}
        onRefresh={handleRefresh}
        onExport={handleExport}
      />

      <Card
        styles={{ body: { padding: 0 } }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={(tab) => onSearchChange({ tab: tab as AdminLogsSearch['tab'], page: 1 })}
          tabBarStyle={{ padding: `0 ${token.padding}px`, marginBottom: 0 }}
          items={[
            { key: 'executions', label: t('logs.tabs.executions') },
            ...(canSeeAudit ? [{ key: 'audit', label: t('logs.tabs.audit') }] : [])
          ]}
        />
        {activeTab === 'audit' ? (
          <ActionLogsTable
            entries={auditQuery.data?.data ?? []}
            total={auditQuery.data?.total ?? 0}
            page={search.page}
            pageSize={search.pageSize}
            loading={auditQuery.isFetching}
            onPageChange={handlePageChange}
          />
        ) : (
          <FlowExecutionsTable
            executions={executionsQuery.data?.data ?? []}
            total={executionsQuery.data?.total ?? 0}
            page={search.page}
            pageSize={search.pageSize}
            loading={executionsQuery.isFetching}
            showTenant={!fixedClientId}
            onPageChange={handlePageChange}
            onOpen={setOpenExecution}
          />
        )}
      </Card>

      <FlowExecutionDrawer execution={openExecution} onClose={() => setOpenExecution(null)} />
    </>
  );
}
